import { useTranslation } from '@msdk/common'
import {
  FieldDefinition,
  FieldType,
  FormListFieldDefinition,
  FormNode,
  ListFieldDefinition,
  ListValue,
  traverseSiblings,
} from '@msdk/definitions'
import {
  KeyValue,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
  uiTokens,
} from '@msdk/ui'
import React, { ReactNode, useCallback, useMemo } from 'react'

import { useFieldLabel } from '../../hooks'
import { ConditionalReadonlyFieldRenderer } from './ConditionalReadonlyFieldRenderer'
import { ReadonlyFieldContainer } from './ReadonlyFieldContainer'

const StyledConditionalReadonlyFieldRenderer = styled(ConditionalReadonlyFieldRenderer)`
  &.ConditionalField-renderer {
    padding: ${uiTokens.spacing.padding.small}px 0;
  }
`

const StyledTableContainer = styled(TableContainer)`
  background-color: transparent;
`

function isFormListFieldDefinition(value: unknown): value is FormListFieldDefinition {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const formDefinition = value as Partial<FormListFieldDefinition>
  return (
    (formDefinition.hideDescription === null ||
      formDefinition.hideDescription === undefined ||
      typeof formDefinition.hideDescription === 'boolean') &&
    (formDefinition.overrideIDML === null ||
      formDefinition.overrideIDML === undefined ||
      typeof formDefinition.overrideIDML === 'string') &&
    (formDefinition.overrideDescription === null ||
      formDefinition.overrideDescription === undefined ||
      typeof formDefinition.overrideDescription === 'string') &&
    (!formDefinition.columns || Array.isArray(formDefinition.columns))
  )
}

function ReadonlyListContent({
  node,
  value,
  fieldDef,
  formDef,
  loading,
  divider,
  inList,
}: {
  node: FormNode
  value?: ListValue
  fieldDef: ListFieldDefinition
  formDef: FormListFieldDefinition
  loading?: boolean
  divider?: boolean
  inList?: boolean
}) {
  const fieldLabel = useFieldLabel(fieldDef, formDef)
  const showLabel = !inList && !formDef.hideDescription
  const t = useTranslation()

  const retrieveLabel = useCallback(
    (fieldDefCB: FieldDefinition) => {
      const labelIDML = formDef.overrideIDML ?? fieldDefCB.idml
      const labelDescription = formDef.overrideDescription ?? fieldDefCB.description
      return t(labelIDML, labelDescription)
    },
    [t, formDef],
  )

  const visibleColumns = useMemo(() => {
    if (!formDef.columns) {
      return undefined
    }
    return new Set(formDef.columns.map((column) => column.name))
  }, [formDef.columns])

  const builtValue = useMemo(() => {
    if (!value) {
      return ''
    }
    const builtRows: ReactNode[] = []
    const builtHeader: ReactNode[] = []

    fieldDef.fields.forEach((field) => {
      if (!visibleColumns || visibleColumns.has(field.name)) {
        builtHeader.push(<TableCell key={field.name}>{retrieveLabel(field)}</TableCell>)
      }
    })

    if (node.firstChild) {
      traverseSiblings(node.firstChild, (rowNode) => {
        const cells: ReactNode[] = []

        traverseSiblings(rowNode.firstChild, (cellNode) => {
          if (!visibleColumns || visibleColumns.has(cellNode.id)) {
            cells.push(
              <TableCell key={cellNode.id}>
                <StyledConditionalReadonlyFieldRenderer
                  node={cellNode}
                  type={cellNode.value.type as FieldType}
                  key={cellNode.id}
                  divider={false}
                  inList={true}
                />
              </TableCell>,
            )
          }
        })

        builtRows.push(
          <TableRow key={rowNode.id} hideDivider>
            {cells}
          </TableRow>,
        )
      })
    }

    return (
      <StyledTableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          {!fieldDef.hideHeader && (
            <TableHeader>
              <TableRow key="header">{builtHeader}</TableRow>
            </TableHeader>
          )}
          <TableBody>{builtRows}</TableBody>
        </Table>
      </StyledTableContainer>
    )
  }, [value, node])

  return (
    <ReadonlyFieldContainer>
      <KeyValue label={showLabel ? fieldLabel : undefined} direction="column" divider={divider} loading={loading}>
        {builtValue}
      </KeyValue>
    </ReadonlyFieldContainer>
  )
}

export function ReadonlyListField({
  node,
  loading,
  divider,
  inList,
}: {
  node: FormNode
  loading?: boolean
  divider?: boolean
  inList?: boolean
}) {
  const { data, fieldDefinition, formDefinition } = node.value
  if (
    !(fieldDefinition instanceof ListFieldDefinition) ||
    (data !== null && data !== undefined && !(data instanceof ListValue)) ||
    !isFormListFieldDefinition(formDefinition)
  ) {
    return null
  }

  return (
    <ReadonlyListContent
      node={node}
      value={data instanceof ListValue ? data : undefined}
      fieldDef={fieldDefinition}
      formDef={formDefinition}
      loading={loading}
      divider={divider}
      inList={inList}
    />
  )
}
